import { Fragment, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { useToggle } from 'hooks';
import { PageState } from 'models/PageState';
import {
  isConfigurable as getIsConfigurable,
  getConfigurator,
  isInstallationConfigurator
} from 'views/ProductPage/configurators/selector';
import { Button } from 'components/Button';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { StandaloneInstallation, StandaloneConfigurator } from 'views/ProductPage/configurators';
import { isMobile } from 'components/Shared/responsive';
import { getVariantId } from 'views/ProductPage/selector';
import { NO_ELEMENT, rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import styles from './variantSelector.scss';
import accordionStyles from 'components/Accordion/styles.scss';
import { AttributeOption } from 'models/AttributeOption';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import { LoadingStatus } from 'models/LoadingStatus';
import { Spinner } from 'components/Shared/Loader';

interface OwnProps {
  className?: string;
  fetching?: boolean;
}
interface MergedProps extends OwnProps {
  isPhone: boolean;
  attributes: AttributeOption[];
  values: ConfiguratorBase['values'];
  variantId?: string;
  isInstallation: boolean;
  accessoryConfigurator?: ReactNode;
  accessoryInstallation?: ReactNode;
}

export const _VariantSelector = ({
  className,
  isPhone,
  attributes,
  values,
  variantId,
  isInstallation,
  accessoryConfigurator,
  accessoryInstallation,
  fetching
}: MergedProps) => {
  const hasSelectedVariant = !!variantId;
  const [isOpen, toggleOpen] = useToggle(!hasSelectedVariant);

  const selectedAttributes = attributes.map(({ id, name, unit }) => {
    return {
      id,
      name,
      value: values?.[id],
      unit
    };
  });

  const showSelectedVariant = !isOpen && hasSelectedVariant;
  const title = showSelectedVariant ? rm('product_selected_variant') : rm('product_select_variant');

  const isInstallationWithoutVariants = isInstallation && attributes.length === 0;

  return (
    <div
      className={cn(
        styles.variantSelector,
        {
          [styles.mobile]: isPhone,
          [styles.blockPointerEvents]: fetching
        },
        className
      )}
      {...getQaDataAttr(`variantSelector`)}
    >
      {isPhone && !isInstallationWithoutVariants && (
        <Button
          onClick={toggleOpen}
          appearance="tertiary"
          className={cn(
            styles.header,
            { [styles.selected]: !isOpen && hasSelectedVariant },
            accordionStyles.container,
            accordionStyles.sectionHeaderContainer
          )}
        >
          <div className={styles.headerContent}>
            <h4 className={accordionStyles.sectionHeader}>{title}</h4>
            {showSelectedVariant && (
              <div className={styles.selectedVariant}>
                {selectedAttributes.map(({ id, name, unit, value }, i) => (
                  <Fragment key={id}>
                    <div className={styles.left} style={getRowStyleForIE(i)}>
                      {name}:
                    </div>
                    <div className={styles.right} style={getRowStyleForIE(i)}>
                      {(values && values[id]) || value} {unit}
                    </div>
                  </Fragment>
                ))}
              </div>
            )}
          </div>
          {showSelectedVariant ? (
            <div className={styles.change}>{rm('product_change_variant')}</div>
          ) : (
            <IconArrow
              className={styles.arrow}
              size="tiny"
              appearance={isOpen ? 'flip-270' : 'flip-90'}
            />
          )}
        </Button>
      )}

      {(isOpen || !isPhone) && (
        <div className={styles.configuratorWrapper}>
          <div className={styles.container}>
            {accessoryConfigurator || <StandaloneConfigurator />}
          </div>
          {fetching && (
            <div className={styles.fetching}>
              <Spinner loading className={styles.loadingSpinner} />
            </div>
          )}
        </div>
      )}

      {isInstallation && (
        <div className={styles.configuratorWrapper}>
          <div className={styles.container}>
            {accessoryInstallation || <StandaloneInstallation />}
          </div>
        </div>
      )}
    </div>
  );
};

// necessary for ie :(
function getRowStyleForIE(i: number): { msGridRows: string } {
  return { msGridRows: `${i + 1}` };
}

const isFetching = ({ currentPage }: PageState) => currentPage.status === LoadingStatus.Fetching;

export const VariantSelector = ({ className }: OwnProps) => {
  const fetching = useSelector(isFetching);
  const isPhone = useSelector(isMobile);
  const isConfigurable = useSelector(({ currentPage }: PageState) =>
    getIsConfigurable(currentPage)
  );
  const configurator = useSelector(({ currentPage }: PageState) => getConfigurator(currentPage));
  const { attributes, values } = configurator;
  const variantId = useSelector(({ currentPage }: PageState) => getVariantId(currentPage));
  const isInstallation = isInstallationConfigurator(configurator);

  if (!isConfigurable) {
    return NO_ELEMENT;
  }

  return (
    <_VariantSelector
      {...{
        className,
        isPhone,
        attributes,
        values,
        variantId,
        isInstallation,
        fetching
      }}
    />
  );
};
